import React from 'react';
import styled from 'styled-components';
import { NavigationLoadingSpinner } from './LoadingSpinner';
import arrow from '../images/arrow.png';

interface GalleryNavigationProps {
    currentIndex: number;
    totalItems: number;
    onPrevious: () => void;
    onNext: () => void;
    isLoading?: boolean;
    disabled?: boolean;
}

const NavigationContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin: 20px 0;
`;

const NavigationButton = styled.button<{ $disabled?: boolean; $loading?: boolean }>`
    background: none;
    border: none;
    cursor: ${props => props.$disabled || props.$loading ? 'not-allowed' : 'pointer'};
    opacity: ${props => props.$disabled || props.$loading ? 0.5 : 1};
    transition: opacity 0.2s ease;
    padding: 8px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;

    &:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.05);
    }

    &:focus {
        outline: 2px solid #007bff;
        outline-offset: 2px;
    }
`;

const ArrowImage = styled.img<{ $flipped?: boolean }>`
    width: 24px;
    height: 24px;
    transform: ${props => props.$flipped ? 'scaleX(-1)' : 'none'};
    transition: transform 0.2s ease;
`;

const NavigationInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 80px;
`;

const ItemCounter = styled.span`
    font-size: 14px;
    color: #666;
    font-weight: 500;
`;

const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
`;

export const GalleryNavigation: React.FC<GalleryNavigationProps> = ({
    currentIndex,
    totalItems,
    onPrevious,
    onNext,
    isLoading = false,
    disabled = false,
}) => {
    const canGoPrevious = currentIndex > 0 && !disabled;
    const canGoNext = currentIndex < totalItems - 1 && !disabled;

    return (
        <NavigationContainer>
            <NavigationButton
                onClick={onPrevious}
                disabled={!canGoPrevious}
                $disabled={!canGoPrevious}
                $loading={isLoading}
                aria-label="Previous image"
            >
                {isLoading ? (
                    <LoadingContainer>
                        <NavigationLoadingSpinner />
                    </LoadingContainer>
                ) : (
                    <ArrowImage src={arrow} alt="Previous" $flipped={true} />
                )}
            </NavigationButton>

            <NavigationInfo>
                <ItemCounter>
                    {currentIndex + 1} of {totalItems}
                </ItemCounter>
            </NavigationInfo>

            <NavigationButton
                onClick={onNext}
                disabled={!canGoNext}
                $disabled={!canGoNext}
                $loading={isLoading}
                aria-label="Next image"
            >
                {isLoading ? (
                    <LoadingContainer>
                        <NavigationLoadingSpinner />
                    </LoadingContainer>
                ) : (
                    <ArrowImage src={arrow} alt="Next" />
                )}
            </NavigationButton>
        </NavigationContainer>
    );
};

// Enhanced navigation with preloading
interface EnhancedGalleryNavigationProps extends GalleryNavigationProps {
    items: Array<{ id: string; full_image_url?: string; thumbnail_url: string }>;
    onNavigate: (index: number) => Promise<void>;
    preloadNext?: boolean;
}

export const EnhancedGalleryNavigation: React.FC<EnhancedGalleryNavigationProps> = ({
    currentIndex,
    totalItems,
    onPrevious,
    onNext,
    isLoading = false,
    disabled = false,
    items: _items,
    onNavigate,
    preloadNext: _preloadNext = true,
}) => {
    const handlePrevious = async () => {
        if (currentIndex > 0) {
            await onNavigate(currentIndex - 1);
            onPrevious();
        }
    };

    const handleNext = async () => {
        if (currentIndex < totalItems - 1) {
            await onNavigate(currentIndex + 1);
            onNext();
        }
    };

    return (
        <GalleryNavigation
            currentIndex={currentIndex}
            totalItems={totalItems}
            onPrevious={handlePrevious}
            onNext={handleNext}
            isLoading={isLoading}
            disabled={disabled}
        />
    );
};
